import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSidebarContext } from '../context/SidebarContext';
import { FaChartBar, FaPhoneAlt, FaRobot, FaProjectDiagram, FaBook, FaLightbulb, FaCreditCard, FaClipboardCheck } from 'react-icons/fa'; // Added FaLightbulb, FaCreditCard, and FaClipboardCheck
import { HiMenu, HiX, HiLogout } from 'react-icons/hi'; // Icons for toggle
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

const AppSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isSidebarOpen, toggleSidebar } = useSidebarContext();
  const { user, signOut } = useAuth();
  const [userRole, setUserRole] = useState(null);

  const trigger = useRef(null);
  const sidebar = useRef(null);

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !isSidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      toggleSidebar(); // Close sidebar
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!isSidebarOpen || keyCode !== 27) return;
      toggleSidebar(); // Close sidebar
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });
  
  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Fetch user role
  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user) {
        setUserRole(null);
        return;
      }

      try {
        const { data: memberships, error } = await supabase
          .from('organization_members')
          .select('role')
          .eq('user_id', user.id)
          .single();

        if (error) {
          console.error('Error fetching user role:', error);
          setUserRole(null);
          return;
        }

        setUserRole(memberships?.role || null);
      } catch (error) {
        console.error('Error fetching user role:', error);
        setUserRole(null);
      }
    };

    fetchUserRole();
  }, [user]);

  // Define your menu items here
  const menuItems = [
    {
      label: 'Calls',
      path: '/dashboard/calls',
      icon: <FaPhoneAlt className="text-lg" />,
      disabled: false,
    },
    {
      label: 'Audits',
      path: '/dashboard/audits',
      icon: <FaClipboardCheck className="text-lg" />,
      disabled: false,
    },
    {
      label: 'Business Insights (Soon)',
      path: '/dashboard/business-insights',
      icon: <FaLightbulb className="text-lg" />,
      disabled: true,
    },
    {
      label: 'Agents',
      path: '/dashboard/agents',
      icon: <FaRobot className="text-lg" />,
      disabled: false,
    },
    {
      label: 'Knowledge (Beta)',
      path: '/dashboard/knowledge-base',
      icon: <FaBook className="text-lg" />,
      disabled: false,
    },
    {
      label: 'Workflows (Beta)',
      path: '/dashboard/workflows',
      icon: <FaProjectDiagram className="text-lg" />,
      disabled: false,
    },
    // Only show billing for admin users
    ...(userRole === 'admin' ? [{
      label: 'Billing',
      path: '/dashboard/billing',
      icon: <FaCreditCard className="text-lg" />,
      disabled: false,
    }] : []),
    {
      label: 'Statistics',
      path: '/dashboard/statistics',
      icon: <FaChartBar className="text-lg" />,
      disabled: false,
    }
  ];

  return (
    <>
      {/* Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        ref={sidebar}
        className={`fixed inset-y-0 left-0 z-50 min-h-screen w-64 sm:w-72 flex flex-col bg-[#1E1E2E] transition-transform duration-300 ease-in-out lg:static lg:translate-x-0 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full justify-between">
          {/* Header */}
          <div className="flex items-center justify-between gap-2 px-4 py-4 sm:px-6 sm:py-6">
            <Link to="/dashboard" className="text-xl sm:text-2xl font-semibold text-white">
              {/* Your logo or app name */}
              Sooot AI
            </Link>

            <button
              ref={trigger}
              onClick={toggleSidebar}
              aria-controls="sidebar"
              aria-expanded={isSidebarOpen}
              className="block lg:hidden text-white"
            >
              {isSidebarOpen ? <HiX size={24} /> : <HiMenu size={24} />}
            </button>
          </div>

          {/* Menu */}
          <nav className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6">
            <div className="flex flex-col gap-y-2">
              {menuItems.map((item, index) => (
                item.disabled ? (
                  <div
                    key={index}
                    className="flex items-center px-3 py-2 text-base sm:text-lg rounded-md text-gray-500 cursor-not-allowed"
                    title="Coming soon"
                  >
                    <span className="mr-3">{item.icon}</span>
                    {item.label}
                  </div>
                ) : (
                  <Link
                    key={index}
                    to={item.path}
                    className={`flex items-center px-3 py-2 text-base sm:text-lg rounded-md transition-colors ${
                      location.pathname === item.path
                        ? 'bg-[#6C63FF] text-white'
                        : 'text-gray-300 hover:bg-[#3A3A4C] hover:text-white'
                    }`}
                  >
                    <span className="mr-3">{item.icon}</span>
                    {item.label}
                  </Link>
                )
              ))}
            </div>
          </nav>

          {/* User Profile and Logout Section */}
          <div className="border-t border-gray-700 bg-[#2A2A3C] px-4 py-4 sm:px-6">
            <div className="flex flex-col gap-y-3">
              <div className="flex items-center gap-x-3">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-full bg-[#6C63FF] flex items-center justify-center text-white font-medium">
                    {user?.email?.charAt(0).toUpperCase()}
                  </div>
                </div>
                <div>
                  <p className="text-sm sm:text-base font-medium text-white truncate">
                    {user?.email?.split('@')[0]}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-400 truncate">
                    {user?.email}
                  </p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-x-2 px-3 py-2 text-sm sm:text-base rounded-md text-gray-300 hover:bg-[#3A3A4C] hover:text-white transition-colors duration-200"
              >
                <HiLogout className="text-lg" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default AppSidebar;
